import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { useAuth } from '../../hooks/useAuth'
import { api, type TrialUsageStatus } from '../../lib/api'
import { isNativeIOS } from '../../mobile/capacitor'
import PageLoader from '../common/PageLoader'
import {
  PAYMENT_REQUIRED_EVENT,
  paymentRecoveryDestination,
  requiresPaymentRecovery,
} from '../../lib/paymentRecovery'

type Props = {
  children: React.ReactNode
}

function getDeniedRoute(status: TrialUsageStatus): string {
  if (isNativeIOS()) {
    return '/trial-activation'
  }
  switch (status.access_status) {
    case 'no_plan':
      return '/trial-activation'
    case 'legacy_free_expired':
      return '/upgrade-required'
    case 'trial_expired':
      return '/pricing'
    default:
      return status.legacy_free_plan ? '/upgrade-required' : '/pricing'
  }
}

export default function CoreAccessRoute({ children }: Props) {
  const { user, loading, profile } = useAuth()
  const userId = user?.id ?? null
  const location = useLocation()
  const navigate = useNavigate()
  const [accessCheck, setAccessCheck] = useState<{
    userId: string
    status: TrialUsageStatus | null
  } | null>(null)
  const [checking, setChecking] = useState(true)
  const status = accessCheck?.userId === userId ? accessCheck.status : null
  const hasCheckedCurrentUser = Boolean(userId && accessCheck?.userId === userId)
  const profileConfirmsAccess = Boolean(
    userId && profile?.id === userId && profile.can_access_app === true
  )

  useEffect(() => {
    let cancelled = false
    let checkingPayment = false
    const recoverPayment = async () => {
      if (!userId || checkingPayment) return
      checkingPayment = true
      try {
        const currentStatus = await api.stripe.getTrialUsageStatus({ cache: 'no-store' })
        if (!cancelled && requiresPaymentRecovery(currentStatus)) {
          navigate(paymentRecoveryDestination(`${location.pathname}${location.search}`), {
            replace: true,
          })
        }
      } catch (error) {
        console.warn('[Access] Failed to verify payment recovery:', error)
      } finally {
        checkingPayment = false
      }
    }
    window.addEventListener(PAYMENT_REQUIRED_EVENT, recoverPayment)
    return () => {
      cancelled = true
      window.removeEventListener(PAYMENT_REQUIRED_EVENT, recoverPayment)
    }
  }, [location.pathname, location.search, navigate, userId])

  useEffect(() => {
    let cancelled = false

    if (!userId) {
      setChecking(false)
      setAccessCheck(null)
      return
    }

    if (profileConfirmsAccess) {
      setChecking(false)
      setAccessCheck(null)
      return
    }

    setChecking(true)
    api.stripe
      .getTrialUsageStatus()
      .then((nextStatus) => {
        if (!cancelled) setAccessCheck({ userId, status: nextStatus })
      })
      .catch((error) => {
        console.warn('[Access] Failed to preflight core access:', error)
        if (!cancelled) setAccessCheck({ userId, status: null })
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })

    return () => {
      cancelled = true
    }
  }, [profileConfirmsAccess, userId])

  if (profileConfirmsAccess) return <>{children}</>

  if (loading || checking || (userId && !hasCheckedCurrentUser)) return <PageLoader />

  if (!user) {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={`/signin?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }

  if (status && !status.can_access_app) {
    const returnTo = `${location.pathname}${location.search}`
    if (requiresPaymentRecovery(status)) {
      return <Navigate to={paymentRecoveryDestination(returnTo)} replace />
    }
    const deniedRoute = getDeniedRoute(status)
    return <Navigate to={`${deniedRoute}?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }

  return <>{children}</>
}
